// features/reports/components/RentabilidadPeriodo.tsx
// ============================================================================
// RENTABILIDAD POR CLASE DEL TRIMESTRE / SEMESTRE (Mabel 23-oct-2026). Suma de la rentabilidad de cada mes ya empezado
// (/finances/presupuesto/rentabilidad-periodo, lib/letsmovePeriodo.js), con las mismas reglas de reparto del mes.
// El punto de equilibrio, la ocupación y el simulador son de un mes: se quedan en la vista «Mes».
// ============================================================================
import { useEffect, useState } from 'react';
import { LeyendaSemaforo, SemaforoDot, SEMAFORO_FILA, pct1, semaforoDe } from './semaforo';
import { FileSpreadsheet, Info } from 'lucide-react';
import { getRentabilidadPeriodo, urlExportRentabilidadPeriodo, RentabilidadPeriodo as RP, TipoPeriodo, RBEsquema } from '../api';
import { moneyEntero as money } from '../../finances/api';
import { readCache, writeCache } from '../../../services/localCache';
import { NavPeriodo, CargandoPeriodo, Actualizando } from './PeriodoSelector';

const card = 'rounded-2xl border border-line bg-surface p-4 shadow-sm';
const th = 'py-2 pr-3 text-left text-xs font-medium uppercase tracking-wide text-content-3 whitespace-nowrap';
const thR = `${th} text-right`;
const td = 'py-1.5 pr-3 text-right tabular-nums whitespace-nowrap';
const navBtn = 'rounded-lg border border-line bg-surface p-2 text-content-2 transition hover:bg-surface-2';
const colorU = (v: number) => (v >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400');
const signo = (v: number) => (v < 0 ? `−${money(Math.abs(v))}` : money(v));

export default function RentabilidadPeriodo({ tipo, mes, movil = false, esquema = 'salones', onMes }: { tipo: TipoPeriodo; mes: string; movil?: boolean; esquema?: RBEsquema; onMes: (ym: string, movil: boolean) => void }) {
  const ck = `rep-rentabilidad-${tipo}-${mes}${movil ? '-movil' : ''}${esquema === 'alumnas' ? '-alumnas' : ''}`;
  const [d, setD] = useState<RP | null>(() => readCache<RP>(ck) ?? null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    const cached = readCache<RP>(ck);
    setD(cached ?? null); setError(null); setCargando(!cached);
    getRentabilidadPeriodo(tipo, mes, movil, esquema)
      .then((r) => { if (!vivo) return; setD(r); writeCache(ck, r); })
      .catch((e: any) => { if (vivo) setError(e?.message || 'No se pudo cargar la rentabilidad del periodo'); })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [tipo, mes, movil, esquema]);

  const barra = (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <NavPeriodo tipo={tipo} mes={mes} movil={movil} onChange={onMes} maxAdelante={0} />
      {cargando && d && <Actualizando />}
      {d && (
        <a href={urlExportRentabilidadPeriodo(tipo, mes, movil, esquema)} download title={`Descargar la tabla del ${tipo}`} className={`${navBtn} flex items-center gap-1.5 text-sm`}>
          <FileSpreadsheet size={16} /> Pasar a Excel
        </a>
      )}
    </div>
  );
  if (error && !d) return <div className="space-y-4">{barra}<p className="rounded-xl border border-line bg-surface p-4 text-sm text-content-2">{error}</p></div>;
  if (!d) return <div className="space-y-4">{barra}<CargandoPeriodo tipo={tipo} mes={mes} movil={movil} /></div>;

  const T = d.totales;
  return (
    <div className={`space-y-4 ${cargando ? 'opacity-60 transition' : 'transition'}`} data-testid="rentabilidad-periodo">
      {barra}
      <p className="text-sm text-content-3"><b className="text-content-2">{d.titulo}</b> · suma de {d.columnas.map((c) => c.corto + (c.estado === 'en_curso' ? ' (en curso)' : '')).join(', ')} · {T.clases} clases</p>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className={card}>
          <div className="text-xs font-medium uppercase tracking-wide text-content-3">Ingreso real de las clases</div>
          <div className="mt-1 font-display text-xl font-bold text-content">{money(T.ingresoReal)}</div>
          <div className="mt-0.5 text-xs text-content-3">del estudio: {money(T.ingresoEstudioReal)}</div>
        </div>
        <div className={card}>
          <div className="text-xs font-medium uppercase tracking-wide text-content-3">Costo total de las clases</div>
          <div className="mt-1 font-display text-xl font-bold text-content">{money(T.costoTotal)}</div>
          <div className="mt-0.5 text-xs text-content-3">docente {money(T.docente)}{esquema === 'alumnas' ? ` · resto por alumnas ${money(T.administrativos)}` : ` · generales ${money(T.generales)} · admin. ${money(T.administrativos)}`}</div>
        </div>
        <div className={card}>
          <div className="text-xs font-medium uppercase tracking-wide text-content-3">Utilidad de las clases</div>
          <div className={`mt-1 font-display text-xl font-bold ${colorU(T.utilidadReal)}`}>{signo(T.utilidadReal)}</div>
          <div className="mt-0.5 text-xs text-content-3">{T.margenPct == null ? '' : `margen ${T.margenPct.toFixed(1)} %`}</div>
        </div>
        <div className={card}>
          <div className="text-xs font-medium uppercase tracking-wide text-content-3">Utilidad del estudio</div>
          <div className={`mt-1 font-display text-xl font-bold ${colorU(T.utilidadEstudioReal)}`}>{signo(T.utilidadEstudioReal)}</div>
          <div className="mt-0.5 text-xs text-content-3">con lo cobrado fuera de clases</div>
        </div>
      </div>

      {/* Mes por mes del estudio */}
      <div className={`${card} overflow-x-auto`}>
        <h3 className="mb-2 font-display text-base font-bold text-content">Mes por mes</h3>
        <table className="w-full text-sm">
          <thead><tr className="border-b border-line"><th className={th}>Mes</th><th className={thR}>Ingreso del estudio</th><th className={thR}>Costo de las clases</th><th className={thR}>Utilidad del estudio</th></tr></thead>
          <tbody>
            {d.columnas.map((c) => {
              const m = d.porMes[c.mes];
              return (
                <tr key={c.mes} className="border-b border-line/60">
                  <td className="py-1.5 pr-3 text-content-2">{c.titulo}{c.estado === 'en_curso' ? ' (en curso)' : ''}</td>
                  <td className={`${td} text-content`}>{money(m.ingresoEstudio)}</td>
                  <td className={`${td} text-content`}>{money(m.costoTotal)}</td>
                  <td className={`${td} ${colorU(m.utilidadEstudio)}`}>{signo(m.utilidadEstudio)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Por clase */}
      <div className={`${card} overflow-x-auto`}>
        <h3 className="mb-2 font-display text-base font-bold text-content">Por clase <span className="text-sm font-normal text-content-3">(de la más a la menos rentable)</span></h3>
        <table className="w-full text-sm" data-testid="rentabilidad-periodo-tabla">
          <thead>
            <tr className="border-b border-line">
              <th className={th}>Clase</th><th className={thR}>Alumnos inscritos</th><th className={thR}>Ingreso real</th><th className={thR}>Sueldo del maestro</th>
              <th className={thR}>Otros gastos directos</th><th className={thR} title={esquema === 'alumnas' ? 'Resto de los gastos repartido por alumnas' : 'Generales + administrativos'}>Gastos compartidos</th><th className={thR}>Costo total</th>
              <th className={thR}>Utilidad real</th><th className={thR}>Margen</th>
            </tr>
          </thead>
          <tbody>
            {d.clases.map((c) => (
              <tr key={c.class_id} className={`border-b border-l-4 border-line/60 ${semaforoDe(c.margenPct, c.costoTotal > 0 || c.ingresoReal > 0) ? SEMAFORO_FILA[semaforoDe(c.margenPct, true)!] : 'border-l-transparent'}`} data-semaforo={semaforoDe(c.margenPct, c.costoTotal > 0 || c.ingresoReal > 0) ?? ''}>
                <td className="py-1.5 pl-2 pr-3">
                  <div className="flex items-center gap-1.5 text-content"><SemaforoDot s={semaforoDe(c.margenPct, c.costoTotal > 0 || c.ingresoReal > 0)} />{c.nombre}{c.archivada ? <span className="ml-1 text-xs text-content-3">(archivada)</span> : null}</div>
                  <div className="text-xs text-content-3">{c.maestros || 'sin maestro'}{c.meses.length < d.meses.length ? ` · ${c.meses.length} de ${d.meses.length} meses` : ''}</div>
                </td>
                <td className={`${td} text-content-2`}>{c.alumnas}</td>
                <td className={`${td} text-content`}>{money(c.ingresoReal)}</td>
                <td className={`${td} text-content-2`}>{money(c.docente)}</td>
                <td className={`${td} text-content-2`}>{money(c.directos)}</td>
                <td className={`${td} text-content-2`} title={`generales ${money(c.generales)} + admin. ${money(c.administrativos)}`}>{money(c.generales + c.administrativos)}</td>
                <td className={`${td} text-content`}>{money(c.costoTotal)}</td>
                <td className={`${td} font-semibold ${colorU(c.utilidadReal)}`}>{signo(c.utilidadReal)}</td>
                <td className={`${td} ${c.margenPct == null ? 'text-content-3' : colorU(c.margenPct)}`}>{pct1(c.margenPct)}</td>
              </tr>
            ))}
            <tr className="bg-surface-2 font-semibold">
              <td className="py-1.5 pr-3 text-content">Total clases</td><td />
              <td className={`${td} text-content`}>{money(T.ingresoReal)}</td>
              <td className={`${td} text-content`}>{money(T.docente)}</td>
              <td className={`${td} text-content`}>{money(T.directos)}</td>
              <td className={`${td} text-content`}>{money(T.generales + T.administrativos)}</td>
              <td className={`${td} text-content`}>{money(T.costoTotal)}</td>
              <td className={`${td} ${colorU(T.utilidadReal)}`}>{signo(T.utilidadReal)}</td>
              <td className={`${td} text-content-2`}>{T.margenPct == null ? '—' : `${T.margenPct.toFixed(1)} %`}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <LeyendaSemaforo />
      <div className="space-y-1 text-xs text-content-3">
        {d.notas.map((n) => <p key={n} className="flex items-start gap-1.5"><Info size={12} className="mt-0.5 shrink-0" />{n}</p>)}
      </div>
    </div>
  );
}
